'use client'

import Link from 'next/link'
import {
  ComponentProps,
  FocusEvent,
  forwardRef,
  MouseEvent,
  TouchEvent,
  useState,
} from 'react'
import {
  usePageInteractive,
  usePagePerformanceLifecycle,
} from '@/hooks/usePagePerformanceLifecycle'
import type { PagePerformanceLifecycle } from '@/lib/page-performance/PagePerformanceLifecycle'
import { resolveLifecycleLinkPrefetch } from '@/lib/page-performance/resolveLifecycleLinkPrefetch'

type LifecycleLinkProps = ComponentProps<typeof Link>
type IntentPrefetchTarget = {
  lifecycle: PagePerformanceLifecycle
  href: LifecycleLinkProps['href']
}

/**
 * 首屏关键周期结束前关闭 Next.js 视口预取；明确的用户意图仍会立即预取。
 */
const LifecycleLink = forwardRef<HTMLAnchorElement, LifecycleLinkProps>(
  function LifecycleLink(
    { href, prefetch, onFocus, onMouseEnter, onTouchStart, ...props },
    ref
  ) {
    const lifecycle = usePagePerformanceLifecycle()
    const pageInteractive = usePageInteractive()
    const [intentPrefetchTarget, setIntentPrefetchTarget] =
      useState<IntentPrefetchTarget | null>(null)
    const hasIntentForCurrentTarget =
      intentPrefetchTarget?.lifecycle === lifecycle &&
      intentPrefetchTarget.href === href
    const shouldDeferPrefetch = Boolean(
      lifecycle && !pageInteractive && !hasIntentForCurrentTarget
    )
    const shouldPrefetch = resolveLifecycleLinkPrefetch(
      prefetch,
      shouldDeferPrefetch
    )

    const prefetchOnIntent = () => {
      if (
        prefetch === false ||
        !lifecycle ||
        pageInteractive ||
        hasIntentForCurrentTarget
      ) {
        return
      }
      setIntentPrefetchTarget({ lifecycle, href })
    }

    const handleFocus = (event: FocusEvent<HTMLAnchorElement>) => {
      onFocus?.(event)
      prefetchOnIntent()
    }
    const handleMouseEnter = (event: MouseEvent<HTMLAnchorElement>) => {
      onMouseEnter?.(event)
      prefetchOnIntent()
    }
    const handleTouchStart = (event: TouchEvent<HTMLAnchorElement>) => {
      onTouchStart?.(event)
      prefetchOnIntent()
    }

    return (
      <Link
        {...props}
        ref={ref}
        href={href}
        prefetch={shouldPrefetch}
        onFocus={handleFocus}
        onMouseEnter={handleMouseEnter}
        onTouchStart={handleTouchStart}
      />
    )
  }
)

LifecycleLink.displayName = 'LifecycleLink'

export default LifecycleLink
